import { useEffect, useMemo, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import {
  HiOutlineArrowDownTray,
  HiOutlineCheckCircle,
  HiOutlineDocumentArrowUp,
  HiOutlineExclamationTriangle,
  HiOutlineTableCells,
} from "react-icons/hi2";

import PageHeader from "../../../components/ui/pageHeader/PageHeader";
import PageCard from "../../../components/ui/pageCard/PageCard";
import PageLoader from "../../../components/ui/pageLoader/PageLoader";
import PageEmpty from "../../../components/ui/pageEmpty/PageEmpty";

import {
  checkExcelFill,
  generateExcelFill,
  previewExcelFill,
  resetExcelFill,
} from "../../../redux/features/excelFill/excelFillSlice";

import { getSchoolYears } from "../../../redux/features/schoolYears/schoolYearSlice";

import "./ExcelFillPage.css";

const DEFAULT_MAPPING = {
  sheetName: "",
  headerRow: 1,
  firstNameColumn: "",
  lastNameColumn: "",
  startDateColumn: "",
};
const getHeaderKey = (header) => {
  if (!header) return "";

  if (typeof header === "string") return header;

  return header.column_letter;
};

const getHeaderLabel = (header) => {
  if (!header) return "";

  if (typeof header === "string") return header;

  return `${header.column_letter} - ${header.value || "Leeg"}`;
};
export default function ExcelFillPage() {
  const dispatch = useDispatch();

  const {
    preview,
    checkResult,
    isPreviewLoading,
    isChecking,
    isGenerating,
    error,
  } = useSelector((state) => state.excelFill);

  const { schoolYears = [] } = useSelector((state) => state.schoolYears || {});

  const [schoolYearId, setSchoolYearId] = useState("");
  const [file, setFile] = useState(null);
  const [mapping, setMapping] = useState(DEFAULT_MAPPING);

  useEffect(() => {
    dispatch(getSchoolYears());

    return () => {
      dispatch(resetExcelFill());
    };
  }, [dispatch]);

  useEffect(() => {
    const activeSchoolYear = schoolYears.find(
      (schoolYear) => schoolYear.is_active,
    );

    if (!schoolYearId && activeSchoolYear?._id) {
      setSchoolYearId(activeSchoolYear._id);
    }
  }, [schoolYears, schoolYearId]);

  useEffect(() => {
    if (!preview) return;

    const defaultSheet = preview.selected_sheet || preview.sheets?.[0] || "";

    setMapping((prev) => ({
      ...prev,
      sheetName: defaultSheet,
      headerRow: preview.header_row || 1,
    }));
  }, [preview]);

  const sheets = useMemo(() => {
    return preview?.sheets || preview?.available_sheets || [];
  }, [preview]);

  const headers = useMemo(() => {
    return Array.isArray(preview?.headers) ? preview.headers : [];
  }, [preview]);

  const fillableFields = useMemo(() => {
    return preview?.fillable_fields || preview?.fields || [];
  }, [preview]);

  const canPreview = Boolean(schoolYearId && file);
  const canCheck = Boolean(
    file &&
    schoolYearId &&
    mapping.sheetName &&
    mapping.headerRow &&
    mapping.firstNameColumn &&
    mapping.lastNameColumn &&
    mapping.startDateColumn,
  );

  const hasBlockingErrors =
    Array.isArray(checkResult?.errors) && checkResult.errors.length > 0;

  const canGenerate = Boolean(checkResult && !hasBlockingErrors);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0];

    setFile(selectedFile || null);
    dispatch(resetExcelFill());
    setMapping(DEFAULT_MAPPING);
  };

  const handlePreview = async () => {
    if (!canPreview) return;

    await dispatch(
      previewExcelFill({
        schoolYearId,
        file,
      }),
    ).unwrap();
  };

  const handleCheck = async () => {
    if (!canCheck) return;

    await dispatch(
      checkExcelFill({
        schoolYearId,
        file,
        sheetName: mapping.sheetName,
        headerRow: mapping.headerRow,
        firstNameColumn: mapping.firstNameColumn,
        lastNameColumn: mapping.lastNameColumn,
        startDateColumn: mapping.startDateColumn,
      }),
    ).unwrap();
  };

  const handleGenerate = async () => {
    if (!canGenerate) return;

    await dispatch(
      generateExcelFill({
        schoolYearId,
        file,
        sheetName: mapping.sheetName,
        headerRow: mapping.headerRow,
        firstNameColumn: mapping.firstNameColumn,
        lastNameColumn: mapping.lastNameColumn,
        startDateColumn: mapping.startDateColumn,
      }),
    ).unwrap();
  };

  if (isPreviewLoading && !preview) {
    return <PageLoader title="Excel analyseren..." />;
  }

  return (
    <div className="excel-fill-page">
      <PageHeader
        eyebrow="POWER"
        title="Excel trajecten aanvullen"
        description="Upload een Excelbestand, koppel de kolommen en download een aangevulde versie. De database wordt niet aangepast."
      />

      {error && <div className="excel-fill-page__error">{error}</div>}

      <div className="excel-fill-page__steps">
        <div className="excel-fill-page__step excel-fill-page__step--active">
          1. Upload
        </div>

        <div
          className={`excel-fill-page__step ${
            preview ? "excel-fill-page__step--active" : ""
          }`}
        >
          2. Preview
        </div>

        <div
          className={`excel-fill-page__step ${
            checkResult ? "excel-fill-page__step--active" : ""
          }`}
        >
          3. Controle
        </div>

        <div
          className={`excel-fill-page__step ${
            canGenerate ? "excel-fill-page__step--active" : ""
          }`}
        >
          4. Download
        </div>
      </div>

      <PageCard className="excel-fill-page__card">
        <div className="excel-fill-page__card-header">
          <div className="excel-fill-page__card-icon">
            <HiOutlineDocumentArrowUp />
          </div>

          <div>
            <h2>Excel uploaden</h2>
            <p>Kies het schooljaar en upload het Excelbestand.</p>
          </div>
        </div>

        <div className="excel-fill-page__form-grid">
          <div className="excel-fill-page__field">
            <label>Schooljaar</label>

            <select
              value={schoolYearId}
              onChange={(event) => setSchoolYearId(event.target.value)}
            >
              <option value="">Selecteer schooljaar</option>

              {schoolYears.map((schoolYear) => (
                <option key={schoolYear._id} value={schoolYear._id}>
                  {schoolYear.name}
                </option>
              ))}
            </select>
          </div>

          <div className="excel-fill-page__field">
            <label>Excel bestand</label>

            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
            />
          </div>
        </div>

        <div className="excel-fill-page__actions">
          <button
            type="button"
            className="excel-fill-page__primary-button"
            disabled={!canPreview || isPreviewLoading}
            onClick={handlePreview}
          >
            {isPreviewLoading ? "Analyseren..." : "Preview maken"}
          </button>
        </div>
      </PageCard>

      {preview && (
        <PageCard className="excel-fill-page__card">
          <div className="excel-fill-page__card-header">
            <div className="excel-fill-page__card-icon">
              <HiOutlineTableCells />
            </div>

            <div>
              <h2>Kolommen koppelen</h2>
              <p>
                Kies het werkblad en de kolommen waarmee dossiers gematcht
                worden.
              </p>
            </div>
          </div>

          <div className="excel-fill-page__form-grid">
            <div className="excel-fill-page__field">
              <label>Sheet</label>

              <select
                value={mapping.sheetName}
                onChange={async (event) => {
                  const newSheet = event.target.value;

                  setMapping((prev) => ({
                    ...prev,
                    sheetName: newSheet,
                    firstNameColumn: "",
                    lastNameColumn: "",
                    startDateColumn: "",
                  }));

                  if (!file || !schoolYearId || !newSheet) return;

                  await dispatch(
                    previewExcelFill({
                      schoolYearId,
                      file,
                      sheetName: newSheet,
                      headerRow: mapping.headerRow,
                    }),
                  );
                }}
              >
                <option value="">Selecteer sheet</option>

                {sheets.map((sheet) => (
                  <option key={sheet} value={sheet}>
                    {sheet}
                  </option>
                ))}
              </select>
            </div>

            <div className="excel-fill-page__field">
              <label>Header row</label>

              <input
                type="number"
                min="1"
                value={mapping.headerRow}
                onChange={async (event) => {
                  const newHeaderRow = Number(event.target.value);

                  setMapping((prev) => ({
                    ...prev,
                    headerRow: newHeaderRow,
                    firstNameColumn: "",
                    lastNameColumn: "",
                    startDateColumn: "",
                  }));

                  if (
                    !file ||
                    !schoolYearId ||
                    !mapping.sheetName ||
                    !newHeaderRow
                  )
                    return;

                  await dispatch(
                    previewExcelFill({
                      schoolYearId,
                      file,
                      sheetName: mapping.sheetName,
                      headerRow: newHeaderRow,
                    }),
                  );
                }}
              />
            </div>

            <div className="excel-fill-page__field">
              <label>Voornaam kolom</label>

              <select
                value={mapping.firstNameColumn}
                onChange={(event) =>
                  setMapping((prev) => ({
                    ...prev,
                    firstNameColumn: event.target.value,
                  }))
                }
              >
                <option value="">Selecteer kolom</option>

                {headers.map((header) => (
                  <option
                    key={getHeaderKey(header)}
                    value={getHeaderKey(header)}
                  >
                    {getHeaderLabel(header)}
                  </option>
                ))}
              </select>
            </div>

            <div className="excel-fill-page__field">
              <label>Familienaam kolom</label>

              <select
                value={mapping.lastNameColumn}
                onChange={(event) =>
                  setMapping((prev) => ({
                    ...prev,
                    lastNameColumn: event.target.value,
                  }))
                }
              >
                <option value="">Selecteer kolom</option>

                {headers.map((header) => (
                  <option
                    key={getHeaderKey(header)}
                    value={getHeaderKey(header)}
                  >
                    {getHeaderLabel(header)}
                  </option>
                ))}
              </select>
            </div>

            <div className="excel-fill-page__field">
              <label>Startdatum kolom</label>

              <select
                value={mapping.startDateColumn}
                onChange={(event) =>
                  setMapping((prev) => ({
                    ...prev,
                    startDateColumn: event.target.value,
                  }))
                }
              >
                <option value="">Selecteer kolom</option>

                {headers.map((header) => (
                  <option
                    key={getHeaderKey(header)}
                    value={getHeaderKey(header)}
                  >
                    {getHeaderLabel(header)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {fillableFields.length > 0 && (
            <div className="excel-fill-page__fillable">
              <h3>Velden die aangevuld kunnen worden</h3>

              <div className="excel-fill-page__badges">
                {fillableFields.map((field) => (
                  <span key={field.key}>{field.label}</span>
                ))}
              </div>
            </div>
          )}

          <div className="excel-fill-page__actions">
            <button
              type="button"
              className="excel-fill-page__primary-button"
              disabled={!canCheck || isChecking}
              onClick={handleCheck}
            >
              {isChecking ? "Controleren..." : "Dossiers controleren"}
            </button>
          </div>
        </PageCard>
      )}

      {checkResult && (
        <PageCard className="excel-fill-page__card">
          <div className="excel-fill-page__card-header">
            <div className="excel-fill-page__card-icon">
              {hasBlockingErrors ? (
                <HiOutlineExclamationTriangle />
              ) : (
                <HiOutlineCheckCircle />
              )}
            </div>

            <div>
              <h2>Controle resultaat</h2>
              <p>Overzicht van gevonden dossiers en eventuele fouten.</p>
            </div>
          </div>

          <div className="excel-fill-page__summary-grid">
            <div>
              <span>Totaal rijen</span>
              <strong>{checkResult.total_rows || 0}</strong>
            </div>

            <div>
              <span>Gematcht</span>
              <strong>{checkResult.matched_rows || 0}</strong>
            </div>

            <div>
              <span>Overgeslagen</span>
              <strong>{checkResult.skipped_rows || 0}</strong>
            </div>

            <div>
              <span>Fouten</span>
              <strong>{checkResult.errors?.length || 0}</strong>
            </div>
          </div>

          {checkResult.errors?.length > 0 ? (
            <div className="excel-fill-page__table-wrapper">
              <table className="excel-fill-page__table">
                <thead>
                  <tr>
                    <th>Rij</th>
                    <th>Fout</th>
                  </tr>
                </thead>

                <tbody>
                  {checkResult.errors.map((item, index) => (
                    <tr key={`${item.row || index}-${item.message}`}>
                      <td>{item.row || "-"}</td>
                      <td>{item.message || item.error || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <PageEmpty
              icon={<HiOutlineCheckCircle />}
              title="Geen blokkerende fouten"
              description="De Excel kan gegenereerd worden."
            />
          )}

          <div className="excel-fill-page__actions">
            <button
              type="button"
              className="excel-fill-page__download-button"
              disabled={!canGenerate || isGenerating}
              onClick={handleGenerate}
            >
              <HiOutlineArrowDownTray />
              <span>
                {isGenerating ? "Genereren..." : "Aangevulde Excel downloaden"}
              </span>
            </button>
          </div>
        </PageCard>
      )}
    </div>
  );
}
